import { ArtboardCornerPosition, ArtboardTextProps } from './types'

/**
 * Wraps canvas text based on a max width and returns an array of text lines.
 */
export const getWrappedCanvasText = (text: string, context: CanvasRenderingContext2D, maxWidth: number): string[] => {
    const words: string[] = text.split(' ')
    const lines: string[] = ['']
    let currentLineWidth = 0

    words.forEach((w, i) => {
        const word = w + (i === words.length - 1 ? '' : ' ')
        const { width } = context.measureText(word)
        if (currentLineWidth + width <= maxWidth) {
            currentLineWidth += width
            lines[lines.length - 1] += word
        } else {
            currentLineWidth = width
            lines.push(word)
        }
    })

    return lines
}

/**
 * Returns a canvas gradient.
 */
export const getCanvasTextFillStyle = (color: string, context: CanvasRenderingContext2D): CanvasGradient | string => {
    const colors = color.split(',')

    if (colors.length < 2) {
        return color
    }

    const gradient = context.createLinearGradient(0, context.canvas.height, context.canvas.width, 0)
    colors.forEach((color, i) => {
        gradient.addColorStop(i / (colors.length - 1), color)
    })

    return gradient
}

/**
 * Generate watermark from caption.
 */
export const generateCaptionCanvas = async (
    { fontSize, fontFamily, fontWeight = 'normal', color, strokeColor, lineHeight = 1.3, text }: ArtboardTextProps,
    maxWidth: number,
    sourceCanvas?: HTMLCanvasElement
): Promise<HTMLCanvasElement> => {
    const canvas = sourceCanvas || document.createElement('canvas')
    const context = canvas.getContext('2d')

    // preload font
    if (document && (document as any).fonts) {
        await (document as any).fonts.load(`${fontSize}px ${fontFamily}`)
    }

    if (context) {
        const textLineHeight = Math.round(fontSize * lineHeight)
        const textPadding = Math.round(fontSize / 2) // add some padding to the sides

        // set font for measurement in getWrappedCanvasText()
        context.font = `${fontWeight} ${fontSize}px ${fontFamily}`

        // get wrapped text lines
        const textLines = getWrappedCanvasText(text, context, maxWidth)

        // auto size canvas to match text size
        canvas.width = (textLines.length > 1 ? maxWidth : context.measureText(text).width) + textPadding * 2
        canvas.height = textLineHeight * textLines.length + textPadding * 2

        // set text styles
        context.textAlign = 'center'
        context.textBaseline = 'middle'
        context.font = `${fontWeight} ${fontSize}px ${fontFamily}` // this is needed twice since resizing canvas will wipe context styles
        context.fillStyle = getCanvasTextFillStyle(color, context)

        // set stroke styles
        if (strokeColor) {
            context.lineWidth = Math.round(fontSize * 0.2)
            context.lineCap = 'round'
            context.lineJoin = 'round'
            context.strokeStyle = getCanvasTextFillStyle(strokeColor, context)
        }

        // draw text
        textLines.forEach((line, i) => {
            const lineX = canvas.width / 2
            const lineY = i * textLineHeight + textLineHeight / 2 + textPadding

            if (strokeColor) {
                context.strokeText(line, lineX, lineY)
            }
            context.fillText(line, lineX, lineY)
        })
    }

    return canvas
}

/**
 * Returns the opposite corner.
 */
export const getOppositeCorner = (corner: ArtboardCornerPosition): ArtboardCornerPosition => {
    switch (corner) {
        case ArtboardCornerPosition.TopLeft:
            return ArtboardCornerPosition.BottomRight
        case ArtboardCornerPosition.TopRight:
            return ArtboardCornerPosition.BottomLeft
        case ArtboardCornerPosition.BottomLeft:
            return ArtboardCornerPosition.TopRight
        case ArtboardCornerPosition.BottomRight:
            return ArtboardCornerPosition.TopLeft
    }
}
